import { useRef } from "react"
import { number, string, bool, shape } from "prop-types"
import Icon from "components/planning_center_icon"
import { formatDate, formatDateTimeShort } from "utils/dates"
import { useKeyListener } from "components/key_listener"

const EventRow = ({
  attendeeCounts,
  churchCenterUrl,
  closeAt,
  scheduledToClose,
  featured: _featured,
  id,
  logoUrl,
  name,
  open,
  openAt,
  scheduledToOpen,
  organization: _organization,
  timeSummary,
  noLogoUrl,
  registerable,
  visibility,
  mostRecentRegistration,
  linkOnly: _linkOnly,
  ccaEnabled,
  ccwEnabled,
  ccaRegistrationsPublished,
  ccwRegistrationsPublished,
  archived
}) => {
  const rowRef = useRef(null)
  const ccwRef = useRef(null)
  const newTab = url => {
    return window.open(url ?? `/signups/${id}`, "_blank")
  }

  const sameTab = url => {
    return Turbolinks.visit(url ?? `/signups/${id}`)
  }

  useKeyListener({
    elref: rowRef,
    register: {
      Enter: sameTab,
      CommandEnter: newTab,
      Click: sameTab,
      CommandClick: newTab
    },
    except: [ccwRef]
  })

  return (
    <tr className="row__clickable" role="button" tabIndex="0" ref={rowRef}>
      <td className="p-1">
        <img
          alt="Signup"
          className="d-b"
          src={logoUrl !== null ? logoUrl : noLogoUrl}
          css={{ borderRadius: 4, width: 120 }}
        />
      </td>
      <td>{name}</td>
      <td className="ws-nw">{timeSummary}</td>
      <td>{registerable ? <span>{attendeeCounts.activeCount}</span> : <span>&mdash;</span>}</td>
      <td>
        {(!ccaRegistrationsPublished && !ccwRegistrationsPublished) ||
        (!ccaEnabled && !ccwEnabled) ? (
          <span>Hidden</span>
        ) : (
          <div>{visibility}</div>
        )}
      </td>
      <td>
        <EventStatusIndicator
          open={open}
          closeAt={closeAt}
          openAt={openAt}
          scheduledToOpen={scheduledToOpen}
          scheduledToClose={scheduledToClose}
          registerable={registerable}
        />
      </td>
      <td>
        {mostRecentRegistration ? (
          formatDateTimeShort(mostRecentRegistration)
        ) : (
          <span>&mdash;</span>
        )}
      </td>
      {ccwRegistrationsPublished && ccwEnabled && (
        <td className="px-1">
          {!archived && (
            <a
              href={churchCenterUrl}
              className="icon-only-btn quiet-app-btn btn2"
              title="View on Church Center"
              target="_blank"
              rel="noreferrer"
              ref={ccwRef}
            >
              <span className="icon p-r t-2p">
                <Icon symbol="general#new-window" />
              </span>
            </a>
          )}
        </td>
      )}
    </tr>
  )
}

const EventStatusIndicator = ({
  open,
  closeAt,
  openAt,
  scheduledToOpen,
  scheduledToClose,
  registerable
}) => {
  let status, openCloseDate

  if (open) {
    status = "Open"

    if (scheduledToClose) {
      openCloseDate = `(until ${formatDate(closeAt)})`
    }
  } else {
    status = "Closed"

    if (scheduledToOpen) {
      openCloseDate = `(until ${formatDate(openAt)})`
    }
  }

  return (
    <div>
      {registerable ? (
        <span>
          {status} {openCloseDate && openCloseDate}
        </span>
      ) : (
        <span>None</span>
      )}
    </div>
  )
}

EventStatusIndicator.propTypes = {
  open: bool,
  closeAt: string,
  openAt: string,
  scheduledToOpen: bool,
  scheduledToClose: bool,
  registerable: bool
}

EventRow.propTypes = {
  attendeeCounts: shape({}),
  churchCenterUrl: string,
  closeAt: string,
  scheduledToClose: bool,
  featured: bool,
  id: number,
  logoUrl: string,
  name: string,
  open: bool,
  openAt: string,
  scheduledToOpen: bool,
  organization: shape({}),
  timeSummary: string,
  noLogoUrl: string,
  registerable: bool,
  visibility: string,
  mostRecentRegistration: string,
  linkOnly: bool,
  ccaEnabled: bool,
  ccwEnabled: bool,
  ccaRegistrationsPublished: bool,
  ccwRegistrationsPublished: bool,
  archived: bool
}

export default EventRow
